import type { ProfilerOnRenderCallback } from "react";
import { baseUrl } from "./api";
import { trackEvent } from "./trackEvent";

type ProbeEntry = { kind: string } & Record<string, unknown>;

interface LongTaskEntry extends PerformanceEntry {
  attribution?: Array<{ containerType?: string; containerName?: string; containerSrc?: string }>;
}

const API = baseUrl ?? "";
const ENDPOINT = `${API}/__perf`;
const FLUSH_MS = 1500;
const INTERACTION_THRESHOLD_MS = 150;
const RENDER_THRESHOLD_MS = 30;
const EXTERNAL_RESOURCE_THRESHOLD_MS = 500;
const SLOW_API_MS = 2000;
const SLOW_INTERACTION_MS = 500;
const SLOW_EVENT_CAP = 20;

const queue: ProbeEntry[] = [];
let timer: number | undefined;

const round = (n: number): number => Math.round(n * 10) / 10;

const flush = (): void => {
  timer = undefined;
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  navigator.sendBeacon(ENDPOINT, JSON.stringify(batch));
};

const push = (entry: ProbeEntry): void => {
  queue.push({ t: Date.now(), page: window.location.pathname, ...entry });
  if (timer === undefined) timer = window.setTimeout(flush, FLUSH_MS);
};

const describeTarget = (node: Node | null): string => {
  if (!(node instanceof Element)) return "unknown";
  const el =
    node.closest(
      "button, a, input, select, textarea, [role=button], [role=tab], li, [data-testid]",
    ) ?? node;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const testId = el.getAttribute("data-testid");
  const text = (el.textContent ?? "").trim().replace(/\s+/g, " ").slice(0, 40);
  return `${tag}${id}${testId ? `[${testId}]` : ""} "${text}"`;
};

const observe = (
  type: string,
  onEntries: (entries: PerformanceEntryList) => void,
  extra: Record<string, unknown> = {},
): void => {
  if (!PerformanceObserver.supportedEntryTypes.includes(type)) return;
  const observer = new PerformanceObserver((list) => onEntries(list.getEntries()));
  observer.observe({ type, buffered: true, ...extra });
};

const onResources = (entries: PerformanceEntryList): void => {
  for (const entry of entries as PerformanceResourceTiming[]) {
    if (entry.name.startsWith(ENDPOINT)) continue;
    const isApi = API !== "" && entry.name.startsWith(API);
    if (!isApi && entry.name.startsWith(window.location.origin)) continue;
    if (!isApi && entry.duration < EXTERNAL_RESOURCE_THRESHOLD_MS) continue;
    push({
      kind: isApi ? "resource" : "resource-ext",
      name: isApi ? entry.name.slice(API.length) : entry.name.slice(0, 160),
      ms: round(entry.duration),
      ttfb: round(entry.responseStart - entry.requestStart),
      dl: round(entry.responseEnd - entry.responseStart),
      bytes: entry.transferSize,
      decoded: entry.decodedBodySize,
      via: entry.initiatorType,
      start: round(entry.startTime),
    });
  }
};

const onEvents = (entries: PerformanceEntryList): void => {
  for (const entry of entries as PerformanceEventTiming[]) {
    if (entry.duration < INTERACTION_THRESHOLD_MS) continue;
    push({
      kind: "event",
      name: entry.name,
      ms: round(entry.duration),
      delay: round(entry.processingStart - entry.startTime),
      proc: round(entry.processingEnd - entry.processingStart),
      paint: round(entry.startTime + entry.duration - entry.processingEnd),
      target: describeTarget(entry.target),
    });
  }
};

const onLongTasks = (entries: PerformanceEntryList): void => {
  for (const entry of entries as LongTaskEntry[]) {
    push({
      kind: "longtask",
      ms: round(entry.duration),
      src: entry.attribution?.[0]?.containerSrc || entry.attribution?.[0]?.containerName || "",
    });
  }
};

const onNavigation = (entries: PerformanceEntryList): void => {
  for (const entry of entries as PerformanceNavigationTiming[]) {
    if (entry.loadEventEnd === 0) continue;
    push({
      kind: "nav",
      dcl: round(entry.domContentLoadedEventEnd),
      load: round(entry.loadEventEnd),
      type: entry.type,
    });
  }
};

const scrubRoute = (name: string): string => {
  const path = name.startsWith(API) ? name.slice(API.length) : name;
  return path
    .split("?")[0]
    .split("/")
    .map((seg) => (/^\d+$/.test(seg) ? ":id" : seg))
    .join("/");
};

let slowEventCount = 0;

export const __resetSlowEventCountForTests = (): void => {
  slowEventCount = 0;
};

const reportSlow = (event: string, data: Record<string, unknown>): void => {
  if (slowEventCount >= SLOW_EVENT_CAP) return;
  if (localStorage.getItem("umami.disabled") === "1") return;
  slowEventCount += 1;
  trackEvent(event, data);
  if (import.meta.env.DEV) console.debug(event, data);
};

export const onResourcesSlow = (entries: PerformanceEntryList): void => {
  for (const entry of entries as PerformanceResourceTiming[]) {
    const isApi = API !== "" && entry.name.startsWith(API);
    if (!isApi || entry.name.startsWith(ENDPOINT) || entry.duration < SLOW_API_MS) continue;
    reportSlow("perf-slow-api", {
      route: scrubRoute(entry.name),
      ms: round(entry.duration),
      ttfb: round(entry.responseStart - entry.requestStart),
    });
  }
};

export const onEventsSlow = (entries: PerformanceEntryList): void => {
  for (const entry of entries as PerformanceEventTiming[]) {
    if (entry.duration < SLOW_INTERACTION_MS) continue;
    reportSlow("perf-slow-interaction", {
      name: entry.name,
      ms: round(entry.duration),
      target: describeTarget(entry.target),
    });
  }
};

export const onRenderProfile: ProfilerOnRenderCallback = (
  _id,
  phase,
  actualDuration,
  baseDuration,
) => {
  if (actualDuration < RENDER_THRESHOLD_MS) return;
  push({ kind: "render", phase, ms: round(actualDuration), base: round(baseDuration) });
};

export const installPerfProbe = (): void => {
  if (typeof PerformanceObserver === "undefined" || API === "") return;
  observe("resource", onResourcesSlow);
  observe("event", onEventsSlow, { durationThreshold: SLOW_INTERACTION_MS });
  if (!import.meta.env.DEV) return;
  observe("resource", onResources);
  observe("event", onEvents, { durationThreshold: INTERACTION_THRESHOLD_MS });
  observe("longtask", onLongTasks);
  observe("navigation", onNavigation);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
};
